import React, { useLayoutEffect, useState } from "react";
import { Query } from "react-apollo";
import { Message } from "semantic-ui-react";
import { Trans } from "react-i18next";
import { startOfDay } from "date-fns";
import { connect } from "react-redux";

import trainingUnitsForCoursePlanQuery from "../../gql/trainingUnitsForCoursePlanQuery";
import ListContainer from "./ListContainer";
import { twoWeeksDown } from "../../common/dateFunctions";

function CoursePlanContainer({ ui }) {
  // Give coursePlanContainer 1px paddingRight if in Iframe
  const isCoursePlanPublic =
    window.location.href.split("/").pop() === "coursePlanPublic";

  const today = startOfDay(new Date());

  const initialPeriod = {
    from: today,
    to: twoWeeksDown(today)
  };

  const [trainingUnits, setTrainingUnits] = useState([]);
  const [appliedFilter, applyFilter] = useState({
    locationId: ui.locationFilter,
    courseCategoryId: ui.categoryFilter,
    ...initialPeriod
  });

  const gqlVariables = {
    filter: {
      ...appliedFilter
    }
  };

  const refreshData = fetchMore => period => {
    loadDataForPeriod(period, fetchMore);
  };

  const loadDataForPeriod = (period, fetchMore) => {
    const gqlUpdateVars = {
      filter: {
        ...gqlVariables.filter,
        from: period.from,
        to: period.to
      }
    };

    return fetchMore({
      variables: gqlUpdateVars,
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) {
          return prev;
        }

        const mergedTrainingUnits = [
          ...fetchMoreResult.trainingUnits,
          ...prev.trainingUnits
        ];

        const newTrainingUnits = mergedTrainingUnits.filter(
          (tu, index, self) => self.findIndex(t => t.id === tu.id) === index
        );

        return {
          ...prev,
          trainingUnits: newTrainingUnits
        };
      }
    });
  };

  useLayoutEffect(() => {
    setTimeout(
      _ => {
        if ("parentIFrame" in window) {
          window.parentIFrame.size();
        }
      },
      // Wait for DOM mutations to settle, otherwise the correct size may not be calculated (this should not be needed, but we haven't found a better solution yet)
      1000
    );
  }, [trainingUnits]);

  return (
    <Query
      query={trainingUnitsForCoursePlanQuery}
      variables={gqlVariables}
      onCompleted={data => {
        setTrainingUnits(data.trainingUnits);
      }}
      fetchPolicy="network-only"
      notifyOnNetworkStatusChange={true}
    >
      {({ loading, error, data, fetchMore, networkStatus }) => {
        if (error) {
          return <Message error content={<Trans i18nKey="errors.noData" />} />;
        }

        const allLocations = !loading ? data.locations : [];
        const courseCategories = !loading ? data.courseCategories : [];

        // Filter deleted locations (hidden) and manual disabled (noShow)
        const filteredLocations = allLocations.filter(
          loc => !loc.hidden && !loc.noShow
        );
        if (appliedFilter.locationId === null) {
          filteredLocations.unshift({ id: null, name: "" });
        }

        // Filtered deleted course categories (hidden) and manual disabled (noShow)
        const filteredCourseCategories = courseCategories.filter(
          cc => !cc.hidden && !cc.noShow
        );
        if (appliedFilter.courseCategoryId === null) {
          filteredCourseCategories.unshift({ id: null, name: "" });
        }

        return (
          <div style={isCoursePlanPublic ? { paddingRight: "1px" } : null}>
            <ListContainer
              initialDisplayPeriod={initialPeriod}
              trainingUnits={trainingUnits}
              isLoading={loading || networkStatus === 4}
              locations={filteredLocations}
              courseCategories={filteredCourseCategories}
              appliedFilter={appliedFilter}
              onUpdateFilter={newFilter => applyFilter(newFilter)}
              onLoadMore={period => loadDataForPeriod(period, fetchMore)}
              onRefreshData={refreshData(fetchMore)}
            />
          </div>
        );
      }}
    </Query>
  );
}

const mapStateToProps = ({ ui }) => ({
  ui
});

export default connect(mapStateToProps)(React.memo(CoursePlanContainer));
